import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useAppSelector } from '../store/hooks';
import { useSearchParams } from 'react-router-dom';
import { ArticleItem } from '../types/typedeclaration';
import LoadmorenewsBox from '../components/LoadmorenewsBox';
import data from '../assets/json/home.json';
import { useSwipe } from '../store/useSwipe';
import fetchData from 'Utilis/request';
// Utility for debouncing a function
// Corrected type for 'timeout' to 'number | undefined' for browser environment
// Utility for debouncing a function
const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timeout: number | undefined; // Correct type for browser environment
  return function(this: any, ...args: any[]) {
    const context = this;
    if (timeout !== undefined) { // Check if it's already set
      window.clearTimeout(timeout); // Explicitly use window.clearTimeout
    }
    timeout = window.setTimeout(() => func.apply(context, args), delay); // Explicitly use window.setTimeout
  };
};
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SearchPage = () => {
  const headerHeight = useAppSelector((state: any) => state.categoryNavigation.headerHeight || 0);
  const fooNavHeight = useAppSelector((state: any) => state.categoryNavigation.fooNavHeight || 0);
  const { onTouchStart, onTouchMove, onTouchEnd } = useSwipe();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('tag') || '';

  const newsContainerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [articles, setArticles] = useState<ArticleItem[]>([]);
  const offsetRef = useRef(0);
  const hasMoreRef = useRef(true);
  const [error, setError] = useState<string | null>(null);

  const isFetchingRef = useRef(false); // New ref to track if a fetch operation is currently in progress

  const darkMode = useAppSelector((state: any) => state.categoryNavigation.darkmode);
  const fontSize = useAppSelector((state: any) => state.categoryNavigation.fontsize);

  // Fetch initial data when search query changes
  useEffect(() => {
    if (!searchQuery) {
      setArticles([]);
      offsetRef.current = 0;
      hasMoreRef.current = true;
      setLoading(false);
      setError(null);
      return;
    }

    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);
      offsetRef.current = 0;
      hasMoreRef.current = true;
      isFetchingRef.current = true; // Set fetching flag

      try {
         const response = await fetchData.getResult(`${API_BASE_URL}search.php`, {
          params: {
            slug: searchQuery.trim(),
            itemnum: '0',
          },
        });
        const initialArticles = response.articles || [];
        setArticles(initialArticles);
        offsetRef.current = initialArticles.length > 0 ? 20 : 0;
        hasMoreRef.current = initialArticles.length > 0;
      } catch (err) {
        console.error("Search error:", err);
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setLoading(false);
        isFetchingRef.current = false; // Reset fetching flag
      }
    };

    fetchInitialData();
  }, [searchQuery]);

  // Fetch more articles
  const fetchMoreArticles = useCallback(async () => {
    // Check both `loading` state (for UI) and `isFetchingRef` (for preventing rapid API calls)
    if (loading || !hasMoreRef.current || !searchQuery || isFetchingRef.current) {
        console.log("Blocking fetchMoreArticles:", { loading, hasMore: hasMoreRef.current, searchQuery, isFetching: isFetchingRef.current });
        return;
    }

    setLoading(true); // Update loading state for UI feedback
    isFetchingRef.current = true; // Set ref to block immediate subsequent calls
    setError(null);

    try {        
      const response = await fetchData.getResult(`${API_BASE_URL}search.php`, {
        params: {
          slug: searchQuery.trim(),
          itemnum: String(offsetRef.current),
        },
      });
   
      const newArticles = response?.articles || [];

      setArticles(prev => [...prev, ...newArticles]);
      offsetRef.current += 20;
      hasMoreRef.current = newArticles.length > 0;

    } catch (err) {
      console.error("Load more error:", err);
      setError(err instanceof Error ? err.message : 'Failed to load more results');
    } finally {
      setLoading(false); // Update loading state for UI feedback
      // Introduce a small delay before resetting isFetchingRef to prevent immediate re-trigger
      setTimeout(() => {
        isFetchingRef.current = false;
      }, 300); // Wait 300ms after fetch completes before allowing another
    }
  }, [searchQuery, loading]);

  // Handle scroll events for infinite loading - DEBOUNCED
  const debouncedHandleScroll = useCallback(
    debounce(() => {
      if (!newsContainerRef.current || loading || !hasMoreRef.current || isFetchingRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = newsContainerRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 300; // Increased threshold

      if (isNearBottom) {
        fetchMoreArticles();
      }
    }, 100), // Debounce by 100ms
    [loading, fetchMoreArticles] // Dependencies for the useCallback wrapping the debounced function
  );

  // Attach scroll event listener
  useEffect(() => {
    const container = newsContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', debouncedHandleScroll);

    return () => {
        if (container) {
            container.removeEventListener('scroll', debouncedHandleScroll);
        }
    };
  }, [debouncedHandleScroll]);

  // Get category title for display
  const categorytitle = data?.categoryButtons.find((item: any) => item.id === searchQuery)?.label ||
                         `Search Results for "${searchQuery}"`;

  return (
    <div className={`font-sans min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}>
      <main
        className="search-container"
        ref={newsContainerRef}        
         style={{ height: `calc(100vh - ${fooNavHeight+10}px)`, overflowY: 'auto', paddingTop:headerHeight+10 }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="news-container">
          {error && (
            <div className={`text-center py-4 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
              {error}
            </div>
          )}

          <LoadmorenewsBox
            darkMode={darkMode}
            fontSize={fontSize}
            articles={articles}
            inlineView={true}
            title={categorytitle}
          />

          {loading && (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}

          {!hasMoreRef.current && articles.length > 0 && (
            <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              No more articles to load
            </div>
          )}

          {!loading && articles.length === 0 && !error && (
            <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              No articles found for "{searchQuery}"
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SearchPage;